import React, { useState, useEffect, useCallback } from "react";
import styled, { createGlobalStyle, ThemeProvider } from "styled-components";
import { FiCalendar, FiCompass, FiMap, FiBriefcase, FiShare2, FiHome } from "react-icons/fi";
import Planner from "./components/Planner";
import { colors, theme } from "./theme";
import { dayTemplatesByCity } from "./data/dayTemplatesByCity";
import NavTabs from "./components/NavTabs";
import MapView from "./components/MapView";
import ExportShare from "./components/ExportShare";
import Onboarding from "./components/Onboarding";
import Explore from "./components/Explore";
import EssentialsPanel from "./components/EssentialsPanel";
import { privateTours } from "./data/privateTours";
import TravelNotes from "./components/TravelNotes";
import HomeTab from "./components/HomeTab";
import { activitiesByCity as initialCityActivities } from "./data/activitiesByCity";

// Default checklist data
const defaultChecklist = {
  documents: [
    { id: 'passport', text: 'Valid passport (6+ months validity)', checked: false },
    { id: 'visa', text: 'Visa (if required)', checked: false },
    { id: 'ehc', text: 'European Health Card (EHIC) or GHIC', checked: false },
    { id: 'travel_insurance', text: 'Travel insurance documents', checked: false },
    { id: 'itinerary', text: 'Travel itinerary and booking confirmations', checked: false },
    { id: 'accommodation', text: 'Accommodation details and addresses', checked: false },
  ],
  money: [
    { id: 'currency', text: 'Euros (€) in cash', checked: false },
    { id: 'credit_card', text: 'Credit/debit cards (notify bank of travel)', checked: false },
    { id: 'emergency_funds', text: 'Emergency cash in USD or EUR', checked: false },
  ],
  electronics: [
    { id: 'adapter', text: 'Type F power adapter (2 round pins)', checked: false },
    { id: 'chargers', text: 'Chargers for all devices', checked: false },
    { id: 'power_bank', text: 'Portable power bank', checked: false },
    { id: 'earbuds', text: 'Headphones/earbuds', checked: false },
  ],
  clothing: [
    { id: 'comfortable_shoes', text: 'Comfortable walking shoes', checked: false },
    { id: 'light_jacket', text: 'Light jacket (even in summer)', checked: false },
    { id: 'swimwear', text: 'Swimwear', checked: false },
    { id: 'sun_protection', text: 'Sun protection (hat, sunglasses, sunscreen)', checked: false },
    { id: 'umbrella', text: 'Compact umbrella or rain jacket', checked: false },
  ],
  health: [
    { id: 'medication', text: 'Prescription medications (with copies of prescriptions)', checked: false },
    { id: 'first_aid', text: 'Basic first aid kit', checked: false },
    { id: 'hand_sanitizer', text: 'Hand sanitizer', checked: false },
    { id: 'masks', text: 'Face masks (if required)', checked: false },
  ],
  misc: [
    { id: 'guidebook', text: 'Travel guidebook or downloaded guides', checked: false },
    { id: 'phrases', text: 'Portuguese phrasebook or app', checked: false },
    { id: 'reusable_bottle', text: 'Reusable water bottle', checked: false },
    { id: 'day_bag', text: 'Day bag for sightseeing', checked: false },
  ]
};

// Load checklist from localStorage or return default if not found
const loadChecklist = () => {
  try {
    const saved = localStorage.getItem('portugal_checklist');
    return saved ? JSON.parse(saved) : defaultChecklist;
  } catch (e) {
    console.error('Failed to load checklist:', e);
    return defaultChecklist;
  }
};

const GlobalStyle = createGlobalStyle`
  /* Modern CSS Reset */
  *, *::before, *::after {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }
  
  /* Smooth Scrolling */
  html {
    scroll-behavior: smooth;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }
  
  /* Base Styles */
  body {
    font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
    background: ${colors.background};
    color: ${colors.text};
    line-height: 1.6;
    min-height: 100vh;
    margin: 0;
    padding: 0;
  }
  
  /* Typography */
  h1, h2, h3, h4, h5, h6 {
    font-family: 'Playfair Display', serif;
    font-weight: 700;
    line-height: 1.3;
    margin-bottom: 0.75em;
    color: ${colors.primary};
  }
  
  /* Links */
  a {
    color: ${colors.secondary};
    text-decoration: none;
    transition: all 0.3s ease;
    font-weight: 500;
    
    &:hover {
      color: ${colors.terracotta};
      text-decoration: underline;
    }
  }
  
  /* Buttons */
  button {
    cursor: pointer;
    font-family: 'Poppins', sans-serif;
    font-size: 1rem;
    font-weight: 500;
    padding: 0.65rem 1.5rem;
    border-radius: 8px;
    border: none;
    transition: all 0.3s ease;
    background: ${colors.primary};
    color: white;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      background: ${colors.azure};
    }
    
    &:active {
      transform: translateY(0);
    }
    
    &:focus {
      outline: none;
      box-shadow: 0 0 0 3px rgba(255, 123, 84, 0.4);
    }
    
    &[disabled] {
      background: ${colors.border};
      cursor: not-allowed;
      transform: none;
      box-shadow: none;
    }
  }
  
  /* Forms */
  input, textarea, select {
    font-family: 'Poppins', sans-serif;
    font-size: 1rem;
    padding: 0.75rem 1rem;
    border: 2px solid ${colors.border};
    border-radius: 8px;
    width: 100%;
    margin-bottom: 1.25rem;
    transition: all 0.3s ease;
    background: white;
    
    &:focus {
      border-color: ${colors.secondary};
      outline: none;
      box-shadow: 0 0 0 3px rgba(255, 123, 84, 0.2);
    }
    
    &::placeholder {
      color: ${colors.muted};
      opacity: 0.7;
    }
  }
  
  /* Scrollbar */
  ::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }
  
  ::-webkit-scrollbar-track {
    background: ${colors.background};
  }
  
  ::-webkit-scrollbar-thumb {
    background: ${colors.primary};
    border-radius: 4px;
    
    &:hover {
      background: ${colors.terracotta};
    }
  }`;

const Wrapper = styled.div`
  min-height: 100vh;
  width: 100%;
  max-width: 100%;
  margin: 0;
  padding: 0;
  position: relative;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
`;

const MainContent = styled.main`
  flex: 1;
  width: 100%;
  max-width: 100%;
  margin: 0 auto;
  padding: 1rem;
  box-sizing: border-box;
  overflow-x: hidden;
  padding-bottom: 80px; /* Space for bottom navigation */
  
  @media (min-width: 768px) {
    max-width: 1400px;
    padding: 1.5rem;
    padding-bottom: 2rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.75rem;
    padding-bottom: 80px;
  }
`;

function App() {
  const [activeTab, setActiveTab] = useState("home");
  
  // Initialize profile state first
  const [profile, setProfile] = React.useState(() => {
    try {
      const data = localStorage.getItem("user_profile");
      return data ? JSON.parse(data) : null;
    } catch {
      return null;
    }
  });
  
  // Skip onboarding by default
  const [showOnboarding, setShowOnboarding] = useState(false);
  
  const handleOnboardingComplete = useCallback(() => {
    setShowOnboarding(false);
    // You can add any additional logic here after onboarding is complete
  }, []);
  
  const [days, setDays] = React.useState(() => {
    try {
      const data = localStorage.getItem("itinerary_days");
      return data ? JSON.parse(data) : [];
    } catch {
      return [];
    }
  });

  // Checklist state and handlers
  const [checklist, setChecklist] = useState(loadChecklist());

  // Save checklist to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem('portugal_checklist', JSON.stringify(checklist));
    } catch (e) {
      console.error('Failed to save checklist:', e);
    }
  }, [checklist]);

  // Toggle checklist item
  const toggleCheckbox = useCallback((category, id) => {
    setChecklist(prev => ({
      ...prev,
      [category]: prev[category].map(item => 
        item.id === id ? { ...item, checked: !item.checked } : item
      )
    }));
  }, []);

  // Calculate overall completion percentage
  const getChecklistProgress = useCallback(() => {
    const allItems = Object.values(checklist).flat();
    const totalItems = allItems.length;
    if (totalItems === 0) return 0;
    const completedItems = allItems.filter(item => item.checked).length;
    return Math.round((completedItems / totalItems) * 100);
  }, [checklist]);

  // Merge static and persisted activities
  const initializeCityActivities = () => {
    const storedStr = localStorage.getItem("cityActivities");
    if (!storedStr) return initialCityActivities;
    let stored = {};
    try { stored = JSON.parse(storedStr); } catch { stored = {}; }
    const merged = {};
    Object.entries(initialCityActivities).forEach(([city, staticList]) => {
      const storedList = Array.isArray(stored[city]) ? stored[city] : [];
      const map = {};
      staticList.forEach(a => { map[a.title] = a; });
      storedList.forEach(a => { map[a.title] = a; });
      merged[city] = Object.values(map);
    });
    // include any extra cities from stored
    Object.entries(stored).forEach(([city, acts]) => {
      if (!merged[city]) merged[city] = acts;
    });
    return merged;
  };
  const [cityActivities, setCityActivities] = React.useState(() => {
    const storedStr = localStorage.getItem("cityActivities");
    if (storedStr) {
      try {
        const stored = JSON.parse(storedStr);
        // Ensure all cities from initial data are present
        const merged = { ...initialCityActivities };
        Object.entries(stored).forEach(([city, acts]) => {
          if (!merged[city]) {
            merged[city] = acts;
          }
        });
        return merged;
      } catch (error) {
        console.error('Error loading stored city activities:', error);
        return initialCityActivities;
      }
    }
    return initialCityActivities;
  });

  React.useEffect(() => {
    try {
      localStorage.setItem("cityActivities", JSON.stringify(cityActivities));
    } catch (error) {
      console.error('Error saving city activities:', error);
    }
  }, [cityActivities]);

  const handleAddActivityToStore = (city, activity) => {
    setCityActivities(prev => ({
      ...prev,
      [city]: prev[city] ? [...prev[city], activity] : [activity]
    }));
  };

  // Keep days in sync with Planner changes
  const handleDaysChange = (newDays) => {
    setDays(newDays);
    try {
      localStorage.setItem("itinerary_days", JSON.stringify(newDays));
    } catch (error) {
      console.error("Failed to save to localStorage:", error);
    }
  };

  // Onboarding completion handler
  const handleOnboarding = (p) => {
    setProfile(p);
    localStorage.setItem("user_profile", JSON.stringify(p));
  };

  // Add sight from Discover to first day
  const handleAddSight = (activity) => {
    // save to central store
    handleAddActivityToStore(activity.city, activity);
    let newDays = days.length > 0 ? [...days] : [{ label: "Day 1", slots: { morning: [], lunch: [], afternoon: [], dinner: [], evening: [] } }];
    if (!newDays[0].slots) {
      newDays[0].slots = { morning: [], lunch: [], afternoon: [], dinner: [], evening: [] };
    }
    newDays[0].slots.morning = [...(newDays[0].slots.morning || []), { title: activity.title, desc: activity.desc, lat: activity.lat, lng: activity.lng }];
    handleDaysChange(newDays);
    setActiveTab("planner");
  };

  // Add activity from map click
  const handleAddActivityFromMap = (activity) => {
    let newDays = days.length > 0 ? [...days] : [{ label: "Day 1", slots: { morning: [], lunch: [], afternoon: [], dinner: [], evening: [] } }];
    if (!newDays[0].slots) {
      newDays[0].slots = { morning: [], lunch: [], afternoon: [], dinner: [], evening: [] };
    }
    newDays[0].slots.morning = [...(newDays[0].slots.morning || []), activity];
    handleDaysChange(newDays);
    setActiveTab("planner");
  };

  const tabs = [
    { key: "home", label: "Home", icon: <FiHome /> },
    { key: "planner", label: "Itinerary", icon: <FiCalendar /> },
    { key: "map", label: "Map", icon: <FiMap /> },
    { key: "explore", label: "Explore", icon: <FiCompass /> },
    { key: "essentials", label: "Essentials", icon: <FiBriefcase /> }
  ];
  
  // State to track if we're on mobile
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);

  // Effect to handle window resize
  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleSelectTemplate = (template) => {
    const newDays = [];
    
    if (template.id === 'custom') {
      // For custom template, create empty days
      for (let i = 0; i < (template.days || 1); i++) {
        newDays.push({
          id: Date.now() + i,
          label: `Day ${i + 1}`,
          slots: {
            morning: [],
            lunch: [],
            afternoon: [],
            dinner: [],
            evening: []
          }
        });
      }
    } else {
      // For pre-defined templates, use the template data from dayTemplatesByCity
      const city = template.city || 'lisbon'; // Use template.city if available, otherwise default to 'lisbon'
      const cityTemplates = dayTemplatesByCity[city.toLowerCase()] || [];
      
      // If we have templates for this city, use them
      if (cityTemplates.length > 0) {
        cityTemplates.forEach((tpl, idx) => {
          newDays.push({
            ...tpl,
            id: Date.now() + idx,
            city: city,
            slots: {
              ...tpl.slots,
              // Ensure all slot arrays are initialized
              morning: [...(tpl.slots.morning || [])],
              lunch: [...(tpl.slots.lunch || [])],
              afternoon: [...(tpl.slots.afternoon || [])],
              dinner: [...(tpl.slots.dinner || [])],
              evening: [...(tpl.slots.evening || [])]
            }
          });
        });
      } else {
        // Fallback to default templates if no city templates found
        const defaultTemplates = {
          '3days': [
            { label: 'Day 1: Arrival & City Tour', slots: { morning: [], lunch: [], afternoon: [], dinner: [], evening: [] } },
            { label: 'Day 2: Full Day Exploration', slots: { morning: [], lunch: [], afternoon: [], dinner: [], evening: [] } },
            { label: 'Day 3: Departure', slots: { morning: [], lunch: [], afternoon: [], dinner: [], evening: [] } }
          ],
          '7days': [
            { label: 'Day 1: Arrival & City Tour', slots: { morning: [], lunch: [], afternoon: [], dinner: [], evening: [] } },
            { label: 'Day 2: Historical Sites', slots: { morning: [], lunch: [], afternoon: [], dinner: [], evening: [] } },
            { label: 'Day 3: Day Trip', slots: { morning: [], lunch: [], afternoon: [], dinner: [], evening: [] } },
            { label: 'Day 4: Local Culture', slots: { morning: [], lunch: [], afternoon: [], dinner: [], evening: [] } },
            { label: 'Day 5: Free Day', slots: { morning: [], lunch: [], afternoon: [], dinner: [], evening: [] } },
            { label: 'Day 6: Nature & Scenery', slots: { morning: [], lunch: [], afternoon: [], dinner: [], evening: [] } },
            { label: 'Day 7: Departure', slots: { morning: [], lunch: [], afternoon: [], dinner: [], evening: [] } }
          ]
        };
        
        const templateDays = defaultTemplates[template.id] || [];
        templateDays.forEach((day, index) => {
          newDays.push({
            ...day,
            id: Date.now() + index,
            city: city
          });
        });
      }
      
      // If it's a private tour template, get it from privateTours
      if (template.isPrivateTour) {
        // Get the city from the template ID (e.g., 'lisbon' from 'lisbon-premium')
        const cityKey = template.id.split('-')[0];
        const selectedTour = privateTours[cityKey];
        
        if (selectedTour) {
          // Format the private tour data to match the day structure
          const formattedDay = {
            id: Date.now(),
            city: selectedTour.city,
            label: selectedTour.label,
            isPrivateTour: true,
            slots: {
              morning: [{
                ...selectedTour,
                title: selectedTour.label,
                desc: selectedTour.description,
                duration: selectedTour.duration,
                price: selectedTour.price,
                rating: selectedTour.rating,
                isPrivateTour: true,
                pois: selectedTour.detailedItinerary.flatMap(section => section.pois || []),
                display: {
                  badge: 'Private Tour',
                  highlights: selectedTour.highlights,
                  includes: selectedTour.includes,
                  excludes: selectedTour.excludes,
                  meetingPoint: selectedTour.meetingPoint,
                  contactInfo: selectedTour.contactInfo
                }
              }],
              lunch: [],
              afternoon: [],
              dinner: [],
              evening: []
            }
          };
          
          newDays.push(formattedDay);
        }
      } else {
        // For regular templates
        const templateDays = templates[template.id] || [];
        templateDays.forEach((day, index) => {
          newDays.push({
            ...day,
            id: Date.now() + index,
            city: city
          });
        });
      }
    }
    
    setDays(newDays);
    setActiveTab("planner");
  };



  return (
    <ThemeProvider theme={colors}>
      <GlobalStyle />
      <Wrapper>
        {showOnboarding ? (
          <Onboarding onComplete={handleOnboardingComplete} />
        ) : (
          <>
            <TravelNotes />
            <NavTabs 
              tabs={tabs} 
              activeTab={activeTab} 
              onTabChange={setActiveTab} 
              isTop={!isMobile}
            />
            <MainContent>
              {activeTab === "home" && (
                <HomeTab 
                  onSelectTemplate={handleSelectTemplate} 
                  setActiveTab={setActiveTab}
                  days={days}
                  activitiesByCity={cityActivities}
                  profile={profile}
                  checklistProgress={getChecklistProgress()}
                />
              )}
              {activeTab === "planner" && (
                <Planner
                  days={days}
                  onDaysChange={handleDaysChange}
                  activitiesByCity={cityActivities}
                  startDate={profile?.startDate}
                />
              )}
              {activeTab === "map" && <MapView days={days} onAddActivity={handleAddActivityFromMap} />}
              {activeTab === "explore" && (
                <Explore 
                  onAdd={handleAddSight} 
                  activitiesByCity={cityActivities} 
                  addActivityToStore={handleAddActivityToStore} 
                />
              )}
              {activeTab === "essentials" && (
                <EssentialsPanel 
                  checklist={checklist} 
                  onToggleCheckbox={toggleCheckbox} 
                />
              )}
              {activeTab === "export" && <ExportShare days={days} />}
            </MainContent>
          </>
        )}
      </Wrapper>
    </ThemeProvider>
  );
}

export default App;
